import React from 'react';
import { Clock, Calendar, List } from 'lucide-react';
import { Chore } from '../types';

interface ChoreListProps {
  chores: Chore[];
}

export const ChoreList: React.FC<ChoreListProps> = ({ chores }) => {
  const getFrequencyColor = (frequency: string) => {
    switch (frequency) {
      case 'Weekly':
        return 'bg-blue-100 text-blue-800';
      case '2x Weekly':
        return 'bg-purple-100 text-purple-800';
      case '3x Weekly':
        return 'bg-indigo-100 text-indigo-800';
      case 'Every 2 Weeks':
        return 'bg-yellow-100 text-yellow-800';
      case 'Monthly':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTimeColor = (minutes: number) => {
    if (minutes <= 5) return 'text-green-600';
    if (minutes <= 15) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
      <div className="flex items-center space-x-2 mb-6">
        <List className="h-6 w-6 text-gray-600" />
        <h2 className="text-2xl font-bold text-gray-900">All Chores</h2>
      </div>
      
      <div className="space-y-4">
        {chores.map(chore => (
          <div key={chore.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{chore.task}</h3>
                
                {chore.subtasks && (
                  <div className="ml-4 space-y-1 mb-3">
                    {chore.subtasks.map(subtask => (
                      <div key={subtask.id} className="flex items-center justify-between text-sm text-gray-600">
                        <span>• {subtask.task}</span>
                        <span className="text-xs text-gray-500">{subtask.timeMinutes}m</span>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getFrequencyColor(chore.frequency)}`}>
                      {chore.frequency}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <span className={`text-sm font-medium ${getTimeColor(chore.timeMinutes)}`}>
                      {chore.timeMinutes} min
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-semibold text-gray-900 mb-2">Legend</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 bg-blue-100 rounded"></span>
              <span>Weekly</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 bg-purple-100 rounded"></span>
              <span>2x Weekly</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 bg-indigo-100 rounded"></span>
              <span>3x Weekly</span>
            </div>
          </div>
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 bg-yellow-100 rounded"></span>
              <span>Every 2 Weeks</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 bg-red-100 rounded"></span>
              <span>Monthly</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 